import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable, of } from 'rxjs';
import { JobFile, JobFileContainer } from '@core/models/job-file';
import { SharedState } from '@core/store/shared.state';
import { ofActionCompleted, Select, Store } from '@ngxs/store';
import { JobGroupJobOperation } from '@shared/models/job-group-job-operation.model';
import { Pagination } from '@shared/models/pagination.model';
import { EntityMap } from '@shared/models/types';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, take, takeUntil, tap } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router, ActivatedRoute } from '@angular/router';
import { GetJpmIdsByJobRefId } from 'app/modules/job-group/store/job-group.actions';
import { JobGroupState } from 'app/modules/job-group/store/job-group.state';
import { Jpm } from '@control-tower/models/jpm';
import { JobType } from '@shared/models/enums/job-type.enum';
import {
  getLoadMasterStatusLabel,
  normalizeLoadMasterStatus,
} from '@shared/utils/load-master-status-display.util';

const DCT_JOB_PROGRESS_STATUSES = ['PARTIALLY_COMPLETED', 'ON_HOLD', 'RECONCILIATION_PENDING'];
@Component({
    selector: 'hmt-job-load-list',
    templateUrl: './job-load-list.component.html',
    styleUrls: ['./job-load-list.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class JobLoadListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('paginator') paginator: MatPaginator;

  @Input() jobs: JobFile[]  = [];
  @Input() tickedJobIdsMap: EntityMap<string, string> = {};
  @Input() title = '';
  @Input() paginationData: Pagination;
  @Input() selectedJobIndex;
  @Input() visibleJobsCount = 7;
  @Input() headerBoarder = true;
  @Input() enableOptions = true;
  @Input() enableMultiSelect = true;
  @Input() logosMap: EntityMap<string, string> = {};
  @Input() loadPercentage$: Observable<EntityMap<string, number>>;

  @Output() selectJobFile: EventEmitter<JobGroupJobOperation> = new EventEmitter();
  @Output() paginate: EventEmitter<PageEvent> = new EventEmitter();
  @Output() search: EventEmitter<string> = new EventEmitter();
  @Output() moveJobFIle: EventEmitter<JobGroupJobOperation> = new EventEmitter();
  @Output() terminateJobFIle: EventEmitter<JobGroupJobOperation> = new EventEmitter();
  @Output() renameJobFIle: EventEmitter<JobGroupJobOperation> = new EventEmitter();
  @Output() tickJobFile: EventEmitter<JobGroupJobOperation> = new EventEmitter();
  @Output() getPercentage: EventEmitter<{orgId: string; jobRefId: string; containerIds: string[]}> = new EventEmitter();
  @Select(JobGroupState.getJpmIds) jpmIds$: Observable<Jpm[]>;
  @Select(SharedState.getSkeletonLoaderStatus) loading$: Observable<boolean>;
  jobFileSearchForm: FormGroup;
  unsubscribe: Subject<void> = new Subject();
  numbers = Array.from({length: 10}, (_, i) => i + 1);
  expandedElement: any = null;

  constructor(private formBuilder: FormBuilder, private router: Router, private store$: Store, private route: ActivatedRoute) {
    this.jobFileSearchForm = this.formBuilder.group({
      searchText: ['']
    });
  }

  ngOnInit(): void {
    // Initialize form with URL search parameter
    const searchText = this.route.snapshot.queryParams.innerPageSearchKey || '';
    this.jobFileSearchForm.patchValue({ searchText }, { emitEvent: false });
    this.initForm();
  }

  ngAfterViewInit(): void {
    // Set paginator to URL page if available
    setTimeout(() => {
      if (this.paginator) {
        const pageIndex = parseInt(this.route.snapshot.queryParams.innerPageNumber) || 0;
        const pageSize = parseInt(this.route.snapshot.queryParams.innerPageSize) || 10;
        this.paginator.pageIndex = pageIndex;
        this.paginator.pageSize = pageSize;
      }
    });
  }

  initForm(): void {
    this.jobFileSearchForm.get('searchText')
      .valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        distinctUntilChanged(),
        debounceTime(500),
        tap(searchText => this.search.emit(searchText))
      )
      .subscribe();
  }

  clearSearch(): void {
    this.jobFileSearchForm.patchValue({searchText: ''});
  }

  clearPaginator(): void {
    setTimeout(() => {
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
    });
  }

  onSelectJob(job, index): void {
    if (this.expandedElement !== job) {
      this.selectedJobIndex = index;
      this.selectJobFile.emit({job, index});
    }
  }

  onTickJobFile(job, index): void {
    this.tickJobFile.emit({job, index});
  }

  tickChange({checked}, job, index) {
    this.tickJobFile.emit({job, index, checked});
  }

  onPaginate(pageEvent: PageEvent) {
    this.paginate.emit(pageEvent);
  }

  moveTo(job, index): void {
    this.moveJobFIle.emit({job, index});
  }

  terminate(job, index): void {
    this.terminateJobFIle.emit({job, index});
  }

  rename(job, index): void {
    this.renameJobFIle.emit({job, index});
  }

  navigateToJpm(jobFile: JobFile, container: JobFileContainer){
    this.store$.dispatch(new GetJpmIdsByJobRefId(jobFile.orgId, jobFile.jobRefId, container?.containerId ?? '')).pipe(
      tap((statedata) => {
        const jpmIds = statedata.jobGroup.jpmIds;
        if(jpmIds.length > 0) {
          if(container?.containerId){
            const jpmId = jpmIds.find(jpm => jpm.containerId === container?.containerId);
            if(jpmId){
              this.router.navigate(
                ['/control-tower'],
                { queryParams: { jobRefId: jobFile.jobRefId, containerId: container.containerId, jpmId: jpmId.jpmId} }
              );
            } else {
              this.router.navigate(
                ['/control-tower'],
                { queryParams: { jobRefId: jobFile.jobRefId, containerId: container.containerId, jpmId: jpmIds[0].jpmId, bookingEngine: true} }
              );
            }
          } else {
            this.router.navigate(
              ['/control-tower'],
              { queryParams: { jobRefId: jobFile.jobRefId, containerId: container.containerId, jpmId: jpmIds[0].jpmId} }
            );
          }
        } else {
          this.router.navigate(
            ['/control-tower'],
            { queryParams: { jobRefId: jobFile.jobRefId, containerId: container.containerId} }
          );
        }
      })
    ).subscribe();
  }

  navigateToLoad(jobFile: JobFile, container: JobFileContainer) { 
    if (container?.jobProgressStatus === 'SCHEDULE_NOTIFIED' ||
      container?.jobProgressStatus === 'COMPLETED' ||
      container?.jobProgressStatus === 'TERMINATED' ||
      container?.jobProgressStatus === 'IN_PROGRESS') {
        this.navigateToJpm(jobFile, container);
    } else if(container?.jobProgressStatus === 'SCHEDULED'){
      if(jobFile?.urlVersion === 'v2'){
        this.router.navigate(
          ['/schedule-v2'],
          { queryParams: { jobRefId: jobFile.jobRefId} }
        );
      } else {
        this.router.navigate(
          ['/schedule'],
          { queryParams: { jobRefId: jobFile.jobRefId} }
        );
      }
    } else if(container?.jobProgressStatus === 'PLANNING'){
      if(jobFile?.urlVersion === 'v2'){
        if(jobFile?.jobType === JobType.DISTRIBUTION){
          this.router.navigate(
            ['/wizard-v2/job-planning'],
            { queryParams: { jobRefId: jobFile.jobRefId} }
          );
        } else {
          this.router.navigate(
            ['/load-management'],
            { queryParams: { jobRefId: jobFile.jobRefId} }
          );
        }
      } else {
        this.router.navigate(
          ['/wizard/container-management'],
          { queryParams: { jobRefId: jobFile.jobRefId, order:0, containerId: container.containerId} }
        );
      }
    }
  }

  getLoadPercentage(jobFile: JobFile, container: JobFileContainer) {
    return this.loadPercentage$;
  }

  toggleRow(jobFile: JobFile, event: Event) {
    event.stopPropagation();
    this.expandedElement = this.expandedElement === jobFile ? null : jobFile;
    const containerIds = jobFile?.containers.map(c => c.containerId);
    if(this.expandedElement){
      this.getPercentage.emit({orgId: jobFile.orgId, jobRefId: jobFile.jobRefId, containerIds});
    }
    if (!jobFile?.containers || jobFile.containers.length === 0) {
      if(jobFile?.urlVersion === 'v2'){
        if(jobFile?.jobFileChain?.size >= 1){
          this.router.navigate(
            ['/load-management'],
            { queryParams: { jobRefId: jobFile.jobRefId, orgId: jobFile.jobFileChain.get(0).orgId, shared: true, sharedJobRefId: jobFile.jobFileChain.get(0).jobRefId} }
          );

        } else {
          if(jobFile?.jobType === JobType.DISTRIBUTION){
            this.router.navigate(
              ['/wizard-v2/job-planning'],
              { queryParams: { jobRefId: jobFile.jobRefId} }
            );
          } else {
            this.router.navigate(
              ['/load-management'],
              { queryParams: { jobRefId: jobFile.jobRefId} }
            );
          }
        }
        
      } else {
        this.router.navigate(
          ['/wizard/job-file'],
          { queryParams: { jobRefId: jobFile.jobRefId, order:0} }
        );
      }
    }
  }

  getLoadStatus(jobFile: JobFile, container: JobFileContainer) {
      if(container?.jobProgressStatus === 'TERMINATED'){
        return 'Terminated';
      }
      if(container?.jobProgressStatus === 'COMPLETED'){
        return 'Completed';
      }
      if(container?.jobProgressStatus === 'IN_PROGRESS'){
        return 'In-Progress';
      }
      if(container?.jobProgressStatus === 'SCHEDULE_NOTIFIED'){
        return 'Scheduled-Notified';
      }
      if(container?.jobProgressStatus === 'SCHEDULED'){
        return 'Scheduled';
      }
      if(container?.jobProgressStatus === 'PLANNING'){
        return 'Planning';
      }
      // Dynamic Control Tower states
      if (DCT_JOB_PROGRESS_STATUSES.includes(normalizeLoadMasterStatus(container?.jobProgressStatus))) {
        return getLoadMasterStatusLabel(container?.jobProgressStatus);
      }
  }

  /** Css class for the status chip, derived from the label with spaces replaced (DCT labels contain spaces). */
  getLoadStatusClass(job: JobFile, container: JobFileContainer): string {
    // identical to qa ('job-state-' + label?.toLowerCase()) for every non-DCT label (none contain spaces)
    return 'job-state-' + this.getLoadStatus(job, container)?.toLowerCase()?.replace(/\s+/g, '-');
  }

  /** DCT: reconciliation / partial completion pending -> yellow progress ring. */
  isDctPending(container: JobFileContainer): boolean {
    const status = normalizeLoadMasterStatus(container?.dctStatus || container?.jobProgressStatus);
    // reconciliation needed only when GPS changed the trip (deviated); a plain partial trip is not yellow
    return status === 'RECONCILIATION_PENDING' || (status === 'PARTIALLY_COMPLETED' && !!container?.deviated);
  }

  activeCT(container: JobFileContainer) {
    const jobProgressStatus = container?.jobProgressStatus.toUpperCase();
    return !(
      jobProgressStatus === 'SCHEDULE_NOTIFIED' ||
      jobProgressStatus === 'COMPLETED' ||
      jobProgressStatus === 'TERMINATED' ||
      jobProgressStatus === 'IN_PROGRESS' ||
      // DCT execution states (only DCT loads carry them)
      DCT_JOB_PROGRESS_STATUSES.includes(jobProgressStatus));
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
